import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import moment from 'moment';
import {
  Card,
  Button,
  Table,
  Input,
  Space,
  Modal,
  Form,
  Select,
  Radio,
  message,
  Popconfirm,
  Statistic,
  Row,
  Col,
  Checkbox
} from 'antd';
import {
  ShareAltOutlined,
  PlusOutlined,
  EditOutlined,
  FormatPainterOutlined,
  DeleteOutlined,
  DownloadOutlined,
  CopyOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Line } from '@ant-design/plots';
import './ShortenLinks.scss';
import {
  shortenLinkList,
  shortenLinkCreate,
  shortenLinkUpdate,
  shortenLinkDelete,
  shortenLinkClicksList
} from '../../modules/shortenLink';
import { accountId, shortenLinkDomains, campaign_channels, shortenLinkActions, notificationEnvs } from '../../config';
import user from '../../modules/user';
import PreviewPageEditor from './PreviewPageEditor';

const PLATFORMS_WITH_TYPE_ID_PARAMS = ['VMB', 'TBONX'];

// Functional component for rendering the chart
const ChartComponent = ({ shortenKey, fetchSevenDayData }) => {
  const [chartData, setChartData] = React.useState([]);
  const [loadingChart, setLoadingChart] = React.useState(true);
  const [totals, setTotals] = React.useState({
    appInstalls: 0,
    reopens: 0,
    redirects: 0,
  });

  React.useEffect(() => {
    fetchSevenDayData(shortenKey)
      .then(data => {
        setChartData(data);
        const totals = data.reduce(
          (acc, item) => {
            if (item.metric === 'App Installs') {
              acc.appInstalls += item.value;
            } else if (item.metric === 'Re-opens') {
              acc.reopens += item.value;
            } else if (item.metric === 'Web opens') {
              acc.redirects += item.value;
            }
            return acc;
          },
          { appInstalls: 0, reopens: 0, redirects: 0 }
        );
        setTotals(totals);
        setLoadingChart(false);
      })
      .catch(error => {
        console.error('Fetch error:', error);
        message.error('Failed to fetch chart data');
        setLoadingChart(false);
      });
  }, [shortenKey, fetchSevenDayData]);

  const config = {
    data: chartData,
    xField: 'date',
    yField: 'value',
    seriesField: 'metric',
    height: 300,
    title: {
      title: 'App Installs, Re-opens, and Web opens Over Last 7 Days',
    },
    xAxis: {
      title: { text: 'Date' },
    },
    yAxis: {
      title: { text: 'Count' },
    },
    legend: {
      position: 'top',
    },
    point: {
      size: 5,
      shape: 'diamond',
    },
    color: ['#1890ff', '#52c41a', '#faad14'],
    loading: loadingChart,
    smooth: true,
  };

  return (
    <div style={{ padding: '20px', background: '#f5f5f5' }}>
      <Row gutter={16}>
        <Col span={18}>
          <Line {...config} />
        </Col>
        <Col span={6}>
          <div style={{ padding: '20px', background: '#fff', borderRadius: '8px', height: '100%' }}>
            <h3>7-Day Totals</h3>
            <Statistic title="App Installs" value={totals.appInstalls} />
            <Statistic title="Re-opens" value={totals.reopens} style={{ marginTop: '16px' }} />
            <Statistic title="Web opens" value={totals.redirects} style={{ marginTop: '16px' }} />
          </div>
        </Col>
      </Row>
    </div>
  );
};

class ShortenLinks extends Component {
  controller = new AbortController();

  constructor(props) {
    super(props);
    this.state = {
      shortenLinks: [],
      loading: true,
      addModalVisible: false,
      editModalVisible: false,
      editPreviewVisible: false,
      configureModalVisible: false,
      currentShortenLink: null,
      searchText: '',
      filterEnabled: null,
      showRulesConfig: false,
      expandedRowKeys: [],
      filterBU: null,
      filterEnv: null,
      sortField: null,
      sortOrder: null,
      sortField: 'createdAt',
      sortOrder: 'descend',
      currentPage: 1,
      pageSize: 10,
    };
  }

  componentDidMount() {
    const { shortenLinks = [] } = this.props;
    if (shortenLinks.length <= 0) {
      this.fetchShortenLinks();
    } else {
      this.afterFetchShortenLinks(shortenLinks);
    }
  }

  componentWillUnmount() {
    this.controller.abort();
  }

  fetchShortenLinks = () => {
    this.setState({ loading: true });
    this.props.shortenLinkList(accountId).then(res => {
      if (!this.controller.signal.aborted) {
        this.afterFetchShortenLinks(res);
      }
    }).catch(error => {
      if (error.name !== 'AbortError') {
        console.error(error);
        message.error('Failed to fetch shortenlinks');
        this.setState({ loading: false });
      }
    });
  }

  afterFetchShortenLinks = (shortenlinks) => {
    this.setState({ shortenLinks: shortenlinks, loading: false });
  }

  handleSearch = (value) => {
    this.setState({
      searchText: value,
      currentPage: 1, // Reset to first page when searching
    });
  }

  handleAddShortenLink = () => {
    this.setState({ addModalVisible: true });
  }

  handleEditShortenLink = (record) => {
    this.setState({
      editModalVisible: true,
      currentShortenLink: record,
    });
  }

  handleEditPreviewPage = (record) => {
    this.setState({
      editPreviewVisible: true,
      currentShortenLink: record,
    });
  }

  convertToCSV = (data) => {
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(obj =>
      Object.values(obj).map(value =>
        typeof value === 'string' ? `"${value.replace(/"/g, '""')}"` : value
      ).join(',')
    );
    return [headers, ...rows].join('\n');
  };

  handleDownloadCSV = (record) => {
    this.fetchAllClicksData(record.shortenKey)
      .then(data => {
        const formattedData = [];
        const dates = [...new Set(data.map(item => item.date))];

        dates.forEach(date => {
          const dateItems = data.filter(item => item.date === date);
          const appInstalls = dateItems.find(item => item.metric === 'App Installs')?.value || 0;
          const reOpens = dateItems.find(item => item.metric === 'Re-opens')?.value || 0;
          const webOpens = dateItems.find(item => item.metric === 'Web opens')?.value || 0;
          const dailyTotal = appInstalls + reOpens + webOpens;

          formattedData.push({
            Date: date,
            'App Installs': appInstalls,
            'Re-opens': reOpens,
            'Web opens': webOpens,
            'Daily Total': dailyTotal
          });
        });

        const summary = {
          Date: 'TOTAL',
          'App Installs': formattedData.reduce((sum, row) => sum + row['App Installs'], 0),
          'Re-opens': formattedData.reduce((sum, row) => sum + row['Re-opens'], 0),
          'Web opens': formattedData.reduce((sum, row) => sum + row['Web opens'], 0),
          'Daily Total': formattedData.reduce((sum, row) => sum + row['Daily Total'], 0)
        };
        formattedData.push(summary);

        const csvContent = this.convertToCSV(formattedData);
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `${record.name}_${record.shortenKey}_clicks.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch(error => {
        console.error('Download error:', error);
        message.error('Failed to download data');
      });
  };

  handleDeleteShortenLink = (id) => {
    this.props.shortenLinkDelete(id)
      .then(() => {
        message.success('ShortenLink deleted successfully');
        this.setState(prevState => ({
          expandedRowKeys: prevState.expandedRowKeys.filter(key => key !== id)
        }));
        this.fetchShortenLinks();
      })
      .catch(error => {
        message.error('Failed to delete shortenlink');
      });
  }

  handleModalCancel = () => {
    this.setState({
      addModalVisible: false,
      editModalVisible: false,
      editPreviewVisible: false,
      configureModalVisible: false,
      currentShortenLink: null,
    });
  }

  handleAddSubmit = (values) => {
    const newShortenLink = {
      name: values.name,
      description: values.description,
      user: accountId,
      key: values.utm_campaign,
      link: values.link,
    };

    this.props.shortenLinkCreate(newShortenLink)
      .then(() => {
        message.success('ShortenLink added successfully');
        this.fetchShortenLinks();
        this.setState({
          addModalVisible: false,
        });
      })
      .catch(error => {
        if (error.response && error.response.data) {
          console.log('CAMPAIGN', error.response.data);
        }
        message.error(error.response.data.message);
      });
  }

  handleEditSubmit = (values) => {
    this.props.shortenLinkUpdate(this.state.currentShortenLink.shortenKey, {
      name: values.name,
      link: values.link,
      description: values.description
    })
      .then(() => {
        message.success('ShortenLink updated successfully');
        this.setState({ editModalVisible: false, currentShortenLink: null });
        this.fetchShortenLinks();
      })
      .catch(error => {
        message.error('Failed to update shortenlink');
      });
  }

  handleEditPreviewPageSubmit = (data) => {
    // Handle the preview page submission
    console.log('Preview page data submitted:', data);
    // You can add your logic here to save the preview page data
    this.setState({
      editPreviewVisible: false
    });
  }

  handleBackToShortenLinks = () => {
    this.setState({ showRulesConfig: false });
  }

  getUniqueBUs = () => {
    const { shortenLinks } = this.state;
    const bus = shortenLinks.map(link => this.getPlatformFromLink(link.link));
    return [...new Set(bus)];
  }

  getUniqueEnvs = () => {
    const { shortenLinks } = this.state;
    const envs = shortenLinks.map(link => this.getEnvformFromLink(link.link));
    return [...new Set(envs)];
  }

  getPlatformFromLink = (link) => {
    try {
      const url = new URL(link);
      const baseUrl = `${url.protocol}//${url.host}${url.pathname.replace(/\/$/, '')}`;

      for (const platform in shortenLinkDomains) {
        for (const env in shortenLinkDomains[platform]) {
          const normalizedDomain = shortenLinkDomains[platform][env].replace(/\/$/, '');
          if (normalizedDomain === baseUrl) {
            return platform;
          }
        }
      }
      return 'Unknown';
    } catch (error) {
      console.error('Error parsing link:', error);
      return 'Unknown';
    }
  }

  getEnvformFromLink = (link) => {
    try {
      const url = new URL(link);
      const baseUrl = `${url.protocol}//${url.host}${url.pathname.replace(/\/$/, '')}`;

      for (const platform in shortenLinkDomains) {
        for (const env in shortenLinkDomains[platform]) {
          const normalizedDomain = shortenLinkDomains[platform][env].replace(/\/$/, '');
          if (normalizedDomain === baseUrl) {
            return env;
          }
        }
      }
      return 'Unknown';
    } catch (error) {
      console.error('Error parsing link:', error);
      return 'Unknown';
    }
  }

  getFilteredShortenLinks = (pagination = {}) => {
    const { shortenLinks, searchText, filterEnabled, filterBU, filterEnv, sortField, sortOrder } = this.state;
    let filtered = [...shortenLinks];

    if (searchText) {
      filtered = filtered.filter(link =>
        link.name.toLowerCase().includes(searchText.toLowerCase()) ||
        link.shortenLink.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    if (filterEnabled !== null) {
      filtered = filtered.filter(link => link.status === (filterEnabled ? 'active' : 'inactive'));
    }

    if (filterBU) {
      filtered = filtered.filter(link => this.getPlatformFromLink(link.link) === filterBU);
    }

    if (filterEnv) {
      filtered = filtered.filter(link => this.getEnvformFromLink(link.link) === filterEnv);
    }

    if (sortField && sortOrder) {
      filtered.sort((a, b) => {
        let aValue = a[sortField];
        let bValue = b[sortField];

        if (sortField === 'createdAt') {
          aValue = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          bValue = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        }

        if (['totalClicks', 'appInstallClicks', 'reOpenClicks', 'redirectClicks'].includes(sortField)) {
          aValue = aValue || 0;
          bValue = bValue || 0;
        }

        if (sortOrder === 'ascend') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
    }

    const { current = 1, pageSize = 10 } = pagination;
    const startIndex = (current - 1) * pageSize;
    return {
      data: filtered.slice(startIndex, startIndex + pageSize),
      total: filtered.length,
    };
  }

  fetchSevenDayData = (shortenKey) => {
    return this.props.shortenLinkClicksList(shortenKey).then(res => {
      const today = moment();
      const sevenDayData = [];

      for (let index = 0; index < 7; index++) {
        const date = today.clone().subtract(index, 'days').format('YYYY-MM-DD');
        const existingEntry = res.find(entry => entry._id === date);
        if (existingEntry) {
          const installs = existingEntry.types.find(entry => entry.type === 'app-install');
          const reOpens = existingEntry.types.find(entry => entry.type === 're-open');
          const redirect = existingEntry.types.find(entry => entry.type === 'redirect');
          sevenDayData.push(
            { date, metric: 'App Installs', value: installs ? installs.count : 0 },
            { date, metric: 'Re-opens', value: reOpens ? reOpens.count : 0 },
            { date, metric: 'Web opens', value: redirect ? redirect.count : 0 }
          );
        } else {
          sevenDayData.push(
            { date, metric: 'App Installs', value: 0 },
            { date, metric: 'Re-opens', value: 0 },
            { date, metric: 'Web opens', value: 0 }
          );
        }
      }
      return Promise.resolve(sevenDayData.reverse());
    }).catch(error => {
      console.error(error);
      message.error('Failed to fetch shortenLinkClicksList');
      throw error;
    });
  }

  fetchAllClicksData = (shortenKey) => {
    return this.props.shortenLinkClicksList(shortenKey, true).then(res => {
      const sortedEntries = [...res].sort((a, b) =>
        moment(b._id).valueOf() - moment(a._id).valueOf()
      );

      const allData = [];

      sortedEntries.forEach(entry => {
        const date = entry._id;
        const installs = entry.types.find(e => e.type === 'app-install');
        const reOpens = entry.types.find(e => e.type === 're-open');
        const redirect = entry.types.find(e => e.type === 'redirect');

        allData.push(
          { date, metric: 'App Installs', value: installs ? installs.count : 0 },
          { date, metric: 'Re-opens', value: reOpens ? reOpens.count : 0 },
          { date, metric: 'Web opens', value: redirect ? redirect.count : 0 }
        );
      });

      return Promise.resolve(allData);
    }).catch(error => {
      console.error(error);
      message.error('Failed to fetch shortenLinkClicksList');
      throw error;
    });
  }

  handleRowClick = (record) => {
    const { expandedRowKeys } = this.state;
    const isExpanded = expandedRowKeys.includes(record.shortenKey);
    this.setState({
      expandedRowKeys: isExpanded ? [] : [record.shortenKey],
    });
  }

  handleTableChange = (pagination, filters, sorter) => {
    this.setState({
      filterBU: filters.platform ? filters.platform[0] : null,
      filterEnv: filters.env ? filters.env[0] : null,
      sortField: sorter.field || null,
      sortOrder: sorter.order || null,
      currentPage: pagination.current,
      pageSize: pagination.pageSize,
    });
  }

  render() {
    const {
      loading,
      addModalVisible,
      editModalVisible,
      editPreviewVisible,
      currentShortenLink,
      showRulesConfig,
      expandedRowKeys,
      currentPage,
      pageSize,
    } = this.state;

    const smallTitleStyle = {
      fontSize: '11px',
    };

    const columns = [
      {
        title: 'BU',
        dataIndex: 'link',
        key: 'platform',
        render: (link) => this.getPlatformFromLink(link),
        filters: this.getUniqueBUs().map(bu => ({ text: bu, value: bu })),
        onFilter: (value, record) => this.getPlatformFromLink(record.link) === value,
        filterMultiple: false,
      },
      {
        title: 'Campaign Name',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: 'Shorten Link',
        dataIndex: 'shortenLink',
        key: 'shortenLink',
        width: 200,
        filters: this.getUniqueEnvs().map(env => ({ text: env, value: env })),
        onFilter: (value, record) => this.getEnvformFromLink(record.link) === value,
        render: (shortenLink) => (
          <Space size="small">
            <span>{shortenLink}</span>
            <Button
              type="default"
              size="small"
              icon={<CopyOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(shortenLink).then(
                  () => {
                    message.success('Link copied to clipboard!');
                  },
                  (err) => {
                    message.error('Failed to copy link');
                    console.error('Copy error:', err);
                  }
                );
              }}
            />
          </Space>
        ),
      },
      {
        title: 'Created',
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (created) => (
          <span>{created ? moment(created).format('YYYY-MM-DD') : 'N/A'}</span>
        ),
        sorter: true,
        width: 120,
        sortDirections: ['ascend', 'descend'],
      },
      {
        title: <span>Clicks</span>,
        dataIndex: 'totalClicks',
        key: 'totalClicks',
        width: 60,
        sorter: true,
        sortDirections: ['ascend', 'descend'],
      },
      {
        title: <span style={smallTitleStyle}>App Installs</span>,
        dataIndex: 'appInstallClicks',
        key: 'appInstallClicks',
        width: 40,
        sorter: true,
        sortDirections: ['ascend', 'descend'],
      },
      {
        title: <span style={smallTitleStyle}>App Opens</span>,
        dataIndex: 'reOpenClicks',
        key: 'reOpenClicks',
        width: 40,
        sorter: true,
        sortDirections: ['ascend', 'descend'],
      },
      {
        title: <span style={smallTitleStyle}>Web Opens</span>,
        dataIndex: 'redirectClicks',
        key: 'redirectClicks',
        width: 40,
        sorter: true,
        sortDirections: ['ascend', 'descend'],
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_, record) => {
          const typeAccount = this.props.userProfile.user.access.filter(item => item.startsWith('campaign:'))?.map(item => item.split(':')[1])[0];
          return (
            <Space size="small">
              <Button
                type="default"
                size="small"
                icon={<EditOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  this.handleEditShortenLink(record);
                }}
                style={{ display: typeAccount === 'viewer' ? 'none' : 'inline-block' }}
              />
              <Button
                type="default"
                size="small"
                icon={<FormatPainterOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  this.handleEditPreviewPage(record);
                }}
                // style={{ display: typeAccount === 'viewer' ? 'none' : 'inline-block' }}
                style={{ display: 'none' }}
              />
              <Button
                type="default"
                size="small"
                icon={<DownloadOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  this.handleDownloadCSV(record);
                }}
              />
              <Popconfirm
                title="Delete this dynamic link?"
                description="This action cannot be undone."
                onConfirm={(e) => {
                  e.stopPropagation();
                  this.handleDeleteShortenLink(record.shortenKey);
                }}
                onCancel={(e) => e.stopPropagation()}
                okText="Yes"
                cancelText="No"
                onClick={(e) => e.stopPropagation()}
              >
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={(e) => e.stopPropagation()}
                  style={{ display: typeAccount === 'viewer' ? 'none' : 'inline-block' }}
                />
              </Popconfirm>
            </Space>
          );
        },
      },
    ];

    const { data, total } = this.getFilteredShortenLinks({
      current: currentPage,
      pageSize: pageSize,
    });

    return (
      <div className="shortenLinks-container">
        <Card className="shortenLinks-card" >
          <div className="shortenLinks-header">
            <div className="shortenLinks-title">
              <ShareAltOutlined className="shortenLinks-icon" />
              <h2>Dynamic Link Management</h2>
            </div>
            <div className="shortenLinks-actions">
              <Space>
                <Input.Search
                  placeholder="Search by name or link"
                  onSearch={this.handleSearch}
                  style={{ width: 250 }}
                />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={this.handleAddShortenLink}
                  style={{ display: this.props.userProfile.user && this.props.userProfile.user.access.filter(item => item.startsWith('campaign:'))?.map(item => item.split(':')[1])[0] === 'viewer' ? 'none' : 'inline-block' }}
                >
                  Add New Link
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={this.fetchShortenLinks}
                >
                  Refresh
                </Button>
              </Space>
            </div>
          </div>

          <Table
            columns={columns}
            dataSource={data}
            rowKey="shortenKey"
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: total,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} links`,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            expandable={{
              expandedRowKeys,
              expandedRowRender: (record) => (
                <ChartComponent
                  shortenKey={record.shortenKey}
                  fetchSevenDayData={this.fetchSevenDayData}
                />
              ),
              onExpand: (expanded, record) => {
                this.setState({
                  expandedRowKeys: expanded ? [record.shortenKey] : [],
                });
              },
            }}
            onRow={(record) => ({
              onClick: () => this.handleRowClick(record),
            })}
            rowClassName={(record) =>
              expandedRowKeys.includes(record.shortenKey) ? 'expanded-row' : ''
            }
            onChange={this.handleTableChange}
          />
        </Card>

        <Modal
          title="Add New ShortenLink"
          open={addModalVisible}
          onCancel={this.handleModalCancel}
          footer={null}
          destroyOnHidden={true}
          width={800}
          className="constrained-modal"
        >
          <ShortenLinkForm onSubmit={this.handleAddSubmit} />
        </Modal>

        <Modal
          title="Edit Dynamic Link"
          open={editModalVisible}
          onCancel={this.handleModalCancel}
          footer={null}
          destroyOnHidden={true}
          width={800}
          className="constrained-modal"
        >
          {currentShortenLink && (
            <ShortenLinkForm
              initialValues={currentShortenLink}
              onSubmit={this.handleEditSubmit}
            />
          )}
        </Modal>


        <Modal
          title="Eidt Preview Page"
          open={editPreviewVisible}
          onCancel={this.handleModalCancel}
          footer={null}
          destroyOnHidden={true}
          width={800}
          className="constrained-modal"
        >
          <PreviewPageEditor
            initialValues={currentShortenLink}
            onSubmit={this.handleEditPreviewPageSubmit} />
        </Modal>
      </div>
    );
  }
}


class ShortenLinkForm extends Component {
  formRef = React.createRef();

  platformUsesTypeId = (platform) => PLATFORMS_WITH_TYPE_ID_PARAMS.includes(platform);

  mapTypeParamToContentType = (typeParam) => {
    if (!typeParam) return undefined;
    const normalized = typeParam.toLowerCase().replace(/\s+/g, '');
    if (normalized === 'movies' || normalized === 'movie') return 'Movie';
    if (normalized === 'tvseries' || normalized === 'tv-series' || normalized === 'tv_series') return 'TVSeries';
    if (normalized === 'songs' || normalized === 'song') return 'songs';
    return undefined;
  };

  mapContentTypeToTypeParam = (contentType) => {
    if (!contentType) return undefined;
    if (contentType === 'Movie') return 'movies';
    if (contentType === 'TVSeries') return 'tvseries';
    if (contentType === 'songs') return 'songs';
    return contentType.toLowerCase();
  };

  // Parse the link and extract parameters
  parseLink = (link) => {
    try {
      const url = new URL(link);
      const params = new URLSearchParams(url.search);
      const baseUrl = `${url.protocol}//${url.host}${url.pathname.replace(/\/$/, '')}`;

      const contentTypeParam = params.get('contentType') || undefined;
      const contentIdParam = params.get('contentId') || undefined;
      const typeParam = params.get('type') || undefined;
      const idParam = params.get('id') || undefined;

      let platform = null;
      let environment = null;
      for (const plat in shortenLinkDomains) {
        for (const env in shortenLinkDomains[plat]) {
          if (shortenLinkDomains[plat][env].replace(/\/$/, '') === baseUrl) {
            platform = plat;
            environment = env;
            break;
          }
        }
        if (platform && environment) break;
      }

      let contentType = contentTypeParam;
      let contentId = contentIdParam;
      const mappedContentType = this.mapTypeParamToContentType(typeParam);
      if (!contentType && mappedContentType) {
        contentType = mappedContentType;
      }
      if (!contentId && idParam) {
        contentId = idParam;
      }

      const actionParam = params.get('action');
      const action = actionParam || undefined;
      const episodeId = params.get('episodeId') || undefined;

      let destination_type = (contentType || contentId) ? 'specific_content' : 'homepage';
      if (destination_type === 'homepage' && actionParam) {
        destination_type = 'custom_action';
      }

      let campaign_channel = params.get('utm_source') || '';
      let custom_channel = '';
      if (campaign_channel && !campaign_channels.includes(campaign_channel)) {
        custom_channel = campaign_channel;
        campaign_channel = 'others';
      }

      return {
        platform,
        environment,
        contentType,
        contentId,
        action,
        episodeId,
        destination_type,
        utm_source: params.get('utm_source') || '',
        utm_medium: params.get('utm_medium') || '',
        utm_campaign: params.get('utm_campaign') || '',
        utm_term: params.get('utm_term') || '',
        utm_content: params.get('utm_content') || '',
        campaign_channel,
        custom_channel,
      };
    } catch (error) {
      console.error('Error parsing link:', error);
      return {};
    }
  };

  componentDidMount() {
    const { initialValues } = this.props;
    if (initialValues) {
      const { link, ...rest } = initialValues;
      const parsedValues = this.parseLink(link);
      this.formRef.current.setFieldsValue({
        ...rest,
        ...parsedValues,
      });
    }
  }

  handleChannelChange = (value) => {
    this.formRef.current.setFieldsValue({
      utm_source: value === 'others' ? '' : value,
    });
  };

  toCamelCase = (str) => {
    const words = str
      .replace(/[^a-zA-Z0-9\s-+]/g, '')
      .toLowerCase()
      .split(/\s+/);

    return words
      .map((word, index) =>
        index === 0
          ? word.charAt(0).toUpperCase() + word.slice(1)
          : word.charAt(0).toUpperCase() + word.slice(1)
      )
      .join('').substring(0, 50);
  };

  handleNameChange = (e) => {
    this.formRef.current.setFieldsValue({
      utm_campaign: this.toCamelCase(e.target.value),
    });
  };

  handlePlatformChange = (platform) => {
    if (!platform || !shortenLinkDomains[platform]) {
      this.formRef.current.setFieldsValue({ environment: undefined });
      return;
    }

    const availableEnvs = Object.keys(shortenLinkDomains[platform]);
    const preferredOrder = notificationEnvs || ['PROD', 'STAG', 'DEV'];
    const defaultEnv =
      preferredOrder.find((env) => availableEnvs.includes(env)) ||
      availableEnvs[0];

    const currentContentType = this.formRef.current.getFieldValue('contentType');
    this.formRef.current.setFieldsValue({
      environment: defaultEnv,
      contentType: platform === 'TBONX' && currentContentType === 'songs' ? undefined : currentContentType,
    });
  };

  render() {
    const { onSubmit, initialValues } = this.props;

    const platformOptions = Object.keys(shortenLinkDomains).map((platform) => ({
      value: platform,
      label: platform,
    }));

    const getEnvironmentOptions = (platform) => {
      if (platform && shortenLinkDomains[platform]) {
        return Object.keys(shortenLinkDomains[platform]).map((env) => ({
          value: env,
          label: env === 'PROD' ? 'Production' : env === 'STAG' ? 'Staging' : 'Development',
        }));
      }
      return [];
    };

    const getFilteredActions = (platform) => {
      if (platform === 'VMX') {
        return shortenLinkActions.filter(action => action !== 'playTrailer');
      } else if (platform === 'VMB') {
        return shortenLinkActions.filter(action => action === 'landOnDetailPage');
      } else {
        return shortenLinkActions;
      }
    };

    const getContentTypeOptions = (platform) => {
      const options = [
        { value: 'Movie', label: 'Movie' },
        { value: 'TVSeries', label: 'TV Series' },
        { value: 'songs', label: 'Songs' },
      ];
      return platform === 'TBONX' ? options.filter(option => option.value !== 'songs') : options;
    };

    return (
      <Form
        ref={this.formRef}
        layout="vertical"
        onFinish={(values) => {
          const baseUrl = shortenLinkDomains[values.platform][values.environment];
          const queryParams = new URLSearchParams();

          if (values.destination_type === 'specific_content') {
            const useTypeIdParams = this.platformUsesTypeId(values.platform);
            if (useTypeIdParams) {
              const typeParam = this.mapContentTypeToTypeParam(values.contentType);
              if (typeParam) queryParams.append('type', typeParam);
              if (values.contentId) queryParams.append('id', values.contentId);
            } else {
              if (values.contentType) queryParams.append('contentType', values.contentType);
              if (values.contentId) queryParams.append('contentId', values.contentId);
            }
            if (values.action) {
              queryParams.append('action', values.action);
            }
            if (values.contentType === 'TVSeries' && values.action === 'playContent' && values.episodeId) {
              queryParams.append('episodeId', values.episodeId);
            }
          } else if (values.destination_type === 'custom_action') {
            if (values.action) {
              queryParams.append('action', values.action);
            }
          }
          if (values.utm_source) queryParams.append('utm_source', values.utm_source);
          if (values.utm_medium) queryParams.append('utm_medium', values.utm_medium);
          if (values.utm_campaign) queryParams.append('utm_campaign', values.utm_campaign);
          if (values.utm_term) queryParams.append('utm_term', values.utm_term);
          if (values.utm_content) queryParams.append('utm_content', values.utm_content);

          const queryString = queryParams.toString();
          const link = queryString ? `${baseUrl}?${queryString}` : baseUrl;

          onSubmit({ ...values, link });
        }}
        initialValues={
          initialValues
            ? { ...initialValues }
            : {
              enabled: true,
            }
        }
        style={{ maxWidth: 800, margin: '0 auto' }}
      >
        {/* Platform and Environment */}
        <Card title="Platform Details" style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="platform"
                label="BU"
                rules={[{ required: true, message: 'Please select a platform' }]}
              >
                <Select
                  placeholder="Select platform"
                  onChange={this.handlePlatformChange}
                  options={platformOptions}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) => prevValues.platform !== currentValues.platform}
              >
                {({ getFieldValue }) => (
                  <Form.Item
                    name="environment"
                    label="Environment"
                    rules={[{ required: true, message: 'Please select an environment' }]}
                  >
                    <Select
                      placeholder="Select environment"
                      options={getEnvironmentOptions(getFieldValue('platform'))}
                      disabled={!getFieldValue('platform')}
                    />
                  </Form.Item>
                )}
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Campaign Name and Description */}
        <Card title="Campaign Details" style={{ marginBottom: 24 }}>
          <Form.Item
            name="name"
            label="Campaign Name"
            rules={[{ required: true, message: 'Please enter campaign name' }]}
          >
            <Input
              placeholder="Enter campaign name"
              onChange={this.handleNameChange}
            />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input placeholder="Enter description" />
          </Form.Item>
        </Card>

        {/* Specific Content Section */}
        <Card title="Content Targeting" style={{ marginBottom: 24 }}>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue, setFieldsValue }) => {
              const platform = getFieldValue('platform');
              const currentDestination = getFieldValue('destination_type') || 'homepage';

              if (platform === 'Club' && currentDestination === 'specific_content') {
                setTimeout(() => {
                  setFieldsValue({ destination_type: 'homepage' });
                }, 0);
              }
              return (
                <Form.Item
                  name="destination_type"
                  initialValue="homepage"
                  style={{ marginBottom: 16 }}
                >
                  <Radio.Group style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <Radio value="homepage" style={{ margin: 0 }}>To launch app / open web homepage</Radio>
                    {platform !== 'Club' && (
                      <Radio value="custom_action" style={{ margin: 0 }}>Action</Radio>
                    )}
                    {platform !== 'Club' && (
                      <Radio value="specific_content" style={{ margin: 0 }}>To a specific content page</Radio>
                    )}
                  </Radio.Group>
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.destination_type !== currentValues.destination_type ||
              prevValues.platform !== currentValues.platform
            }
          >
            {({ getFieldValue }) => {
              const destinationType = getFieldValue('destination_type');
              const platform = getFieldValue('platform');

              if (platform === 'Club' && destinationType !== 'homepage') {
                setTimeout(() => {
                  this.formRef.current.setFieldsValue({
                    destination_type: 'homepage'
                  });
                }, 0);
                return null;
              }

              if (destinationType === 'custom_action') {
                return (
                  <div style={{ padding: 16, background: '#fafafa', borderRadius: 4 }}>
                    <Form.Item
                      name="action"
                      label="Action Value"
                      rules={[{ required: true, message: 'Please enter action value' }]}
                    >
                      <Input placeholder="Example: winback" />
                    </Form.Item>
                  </div>
                );
              }

              if (destinationType === 'specific_content') {
                return (
                  <div style={{ padding: 16, background: '#fafafa', borderRadius: 4 }}>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          noStyle
                          shouldUpdate={(prevValues, currentValues) =>
                            prevValues.platform !== currentValues.platform
                          }
                        >
                          {({ getFieldValue }) => (
                            <Form.Item
                              name="contentType"
                              label="Content Type"
                              rules={[{ required: true, message: 'Please select content type' }]}
                            >
                              <Select placeholder="Select content type">
                                {getContentTypeOptions(getFieldValue('platform')).map(option => (
                                  <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                  </Select.Option>
                                ))}
                              </Select>
                            </Form.Item>
                          )}
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          noStyle
                          shouldUpdate={(prevValues, currentValues) =>
                            prevValues.contentType !== currentValues.contentType
                          }
                        >
                          {({ getFieldValue }) => (
                            <Form.Item
                              name="contentId"
                              label="Content ID"
                              rules={[{ required: true, message: 'Please enter content ID' }]}
                            >
                              <Input
                                placeholder={
                                  getFieldValue('contentType') === 'Movie'
                                    ? 'Example: A00112' : getFieldValue('contentType') === 'TVSeries'
                                      ? 'Example: AVENUES OF THE DIAMOND-78'
                                      : getFieldValue('contentType') === 'songs'
                                        ? 'Example: song ID'
                                        : 'Please enter content ID'
                                }
                              />
                            </Form.Item>
                          )}
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) => prevValues.platform !== currentValues.platform}
                    >
                      {({ getFieldValue }) => (
                        <Form.Item name="action" label="Action">
                          <Select placeholder="Select action" allowClear>
                            {getFilteredActions(getFieldValue('platform')).map((action) => (
                              <Select.Option key={action} value={action}>
                                {action === 'landOnDetailPage'
                                  ? 'Land on content detail page'
                                  : action === 'playContent'
                                    ? 'Play Content'
                                    : action === 'IAP'
                                      ? 'In-App Purchase (For plus content)'
                                      : 'Play Trailer'}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      )}
                    </Form.Item>

                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.contentType !== currentValues.contentType ||
                        prevValues.action !== currentValues.action
                      }
                    >
                      {({ getFieldValue }) => {
                        const contentType = getFieldValue('contentType');
                        const action = getFieldValue('action');

                        if (contentType === 'TVSeries' && action === 'playContent') {
                          return (
                            <Form.Item
                              name="episodeId"
                              label="Episode ID"
                              rules={[{ required: true, message: 'Please enter episode ID' }]}
                            >
                              <Input placeholder="Example: P02819 (Episode ID)" />
                            </Form.Item>
                          );
                        }
                        return null;
                      }}
                    </Form.Item>
                  </div>
                );
              }

              return null;
            }}
          </Form.Item>
        </Card>

        {/* Campaign Channel */}
        <Card title="Campaign Channel" style={{ marginBottom: 24 }}>
          <Form.Item
            name="campaign_channel"
            label="Campaign Channel"
            rules={[{ required: true, message: 'Please select or enter a campaign channel' }]}
          >
            <Select
              placeholder="Select campaign channel"
              onChange={this.handleChannelChange}
              popupRender={(menu) => (
                <>
                  {menu}
                  <Select.Option value="others">Others</Select.Option>
                </>
              )}
            >
              {campaign_channels.map((channel) => (
                <Select.Option key={channel} value={channel}>
                  {channel}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.campaign_channel !== currentValues.campaign_channel
            }
          >
            {({ getFieldValue }) =>
              (getFieldValue('campaign_channel') || '').toLowerCase() === 'others' ? (
                <Form.Item
                  name="custom_channel"
                  label="Custom Channel"
                  rules={[{ required: true, message: 'Please enter custom channel' }]}
                >
                  <Input
                    placeholder="Enter custom channel"
                    onChange={(e) =>
                      this.formRef.current.setFieldsValue({
                        utm_source: this.toCamelCase(e.target.value),
                      })
                    }
                  />
                </Form.Item>
              ) : null
            }
          </Form.Item>
        </Card>

        {/* UTM Tracking Parameters */}
        <Card title="UTM Tracking Parameters" style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="utm_source"
                label="UTM Source"
                rules={[{ message: 'Please enter UTM source' }]}
              >
                <Input disabled placeholder="From campaign channel" />
              </Form.Item>
              <Form.Item
                name="utm_campaign"
                label="UTM Campaign"
                rules={[{ message: 'Please enter UTM campaign' }]}
              >
                <Input disabled placeholder="From campaign name" />
              </Form.Item>
              <Form.Item name="utm_content" label="UTM Content">
                <Input placeholder="Enter UTM content" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="utm_medium" label="UTM Medium">
                <Input placeholder="Enter UTM medium" />
              </Form.Item>
              <Form.Item name="utm_term" label="UTM Term">
                <Input placeholder="Enter UTM term" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Form Actions */}
        <Form.Item style={{ textAlign: 'right', marginTop: 24 }}>
          <Space>
            <Button onClick={() => this.formRef.current.resetFields()}>
              Reset
            </Button>
            <Button type="primary" htmlType="submit">
              {initialValues ? 'Update' : 'Add'} Dynamic Link
            </Button>
          </Space>
        </Form.Item>
      </Form>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    shortenLinks: state.shortenLink.shortenLinks,
    userProfile: state.user.userProfile,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      shortenLinkList,
      shortenLinkCreate,
      shortenLinkUpdate,
      shortenLinkDelete,
      shortenLinkClicksList
    },
    dispatch
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(ShortenLinks);
